import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useTheme } from '@/app/contexts/ThemeContext';
import { useGetUserInfoQuery, useSignOutMutation } from '@/features/auth/api/authApi';
import { getCookie } from '@/shared/services/storage/cookieService';
import { ROUTES } from '@/shared/constants/routes';

const Header = () => {
  const { language, toggleLanguage, translations, isRTL, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  
  // Check if both tokens exist in cookies
  const accessToken = getCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME);
  const refreshToken = getCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME);
  const hasTokens = !!(accessToken && refreshToken);
  
  // Fetch user info only if tokens exist
  const { data: userInfo, isLoading: isLoadingUserInfo } = useGetUserInfoQuery(undefined, {
    skip: !hasTokens
  });
  
  // Sign out mutation
  const [signOut] = useSignOutMutation();
  
  // Fake data for progress bar
  const remainingDays = 15; // Fake remaining days
  const totalDays = 30; // Fake total days
  const progressPercentage = Math.max(0, Math.min(100, (remainingDays / totalDays) * 100));
  
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };
  
  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowPopover(false);
      }
    };
    
    if (showPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPopover]);

  // SEO titles for alt text
  const faSEOTitle = 'کیوردیاب | ابزار تحقیق کلمات کلیدی برای سئو و اینستاگرام';
  const enSEOTitle = 'KeywordYab | Keyword Research Tool for SEO & Instagram';
  const logoSrc = language === 'fa' ? '/img/logo-fa.png' : '/img/logo-en.png';
  const logoAlt = language === 'fa' ? faSEOTitle : enSEOTitle;

  return (
    <header 
      className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo and Navigation */}
          <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Navigation Links - در RTL قبل از لوگو، در LTR بعد از لوگو */}
            {isRTL && (
              <nav className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Link
                  to={language === 'fa' ? ROUTES.PRICING : ROUTES.PRICING_EN}
                  className={`text-sm font-medium hover:text-primary transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {t('pricing.title')}
                </Link>
              </nav>
            )}

            <Link 
              to={language === 'fa' ? ROUTES.HOME : ROUTES.HOME_EN}
              className="flex items-center hover:opacity-80 transition-opacity"
              aria-label={logoAlt}
            >
              <img 
                src={logoSrc} 
                alt={logoAlt}
                className="h-10 md:h-12 w-auto"
              />
            </Link>

            {/* Navigation Links - در LTR بعد از لوگو */}
            {!isRTL && (
              <nav className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Link
                  to={language === 'fa' ? ROUTES.PRICING : ROUTES.PRICING_EN}
                  className={`text-sm font-medium hover:text-primary transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {t('pricing.title')}
                </Link>
              </nav>
            )}
          </div>

          {/* Controls */}
          <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* User Info or Login/Signup Button */}
            {hasTokens ? (
              isLoadingUserInfo ? (
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {/* Loading Avatar Skeleton */}
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
                  {/* Loading Name Skeleton */}
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
              ) : userInfo?.data ? (
                <div className="relative" ref={popoverRef}>
                  <button
                    onClick={() => setShowPopover(!showPopover)}
                    className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''} hover:opacity-80 transition-opacity`}
                  >
                    {/* User Avatar */}
                    {userInfo.data.image ? (
                      <img
                        src={userInfo.data.image}
                        alt={`${userInfo.data.first_name || ''} ${userInfo.data.last_name || ''}`}
                        className="w-10 h-10 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600 cursor-pointer"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-medium text-sm cursor-pointer">
                        {(userInfo.data.first_name?.[0] || '').toUpperCase()}
                        {(userInfo.data.last_name?.[0] || '').toUpperCase()}
                      </div>
                    )}
                    {/* User Name */}
                    <span className="text-gray-700 dark:text-gray-300 text-sm">
                      {userInfo.data.first_name} {userInfo.data.last_name}
                    </span>
                  </button>
                
                {/* Popover */}
                {showPopover && (
                  <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden`}>
                    {/* User Info Section */}
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        {userInfo.data.image ? (
                          <img
                            src={userInfo.data.image}
                            alt={`${userInfo.data.first_name || ''} ${userInfo.data.last_name || ''}`}
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-medium">
                            {(userInfo.data.first_name?.[0] || '').toUpperCase()}
                            {(userInfo.data.last_name?.[0] || '').toUpperCase()}
                          </div>
                        )}
                        <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {userInfo.data.first_name} {userInfo.data.last_name}
                          </p>
                          {userInfo.data.email && (
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {userInfo.data.email}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar Section */}
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="mb-2">
                        <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {t('common.daysRemaining')}
                        </span>
                      </div>
                      <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
                        <div
                          className={`h-3 rounded-full transition-all ${
                            remainingDays > 7 
                              ? 'bg-green-500' 
                              : remainingDays > 3 
                              ? 'bg-yellow-500' 
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {remainingDays} روز باقی‌مانده از {totalDays} روز
                      </p>
                    </div>

                    {/* Menu Items */}
                    <div className="p-2">
                      <Link
                        to={ROUTES.ORDERS}
                        onClick={() => setShowPopover(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          isRTL ? 'flex-row-reverse' : ''
                        }`}
                      >
                        <svg
                          className="w-5 h-5 text-gray-600 dark:text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                          />
                        </svg>
                        <span className={`flex-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          سفارشات من
                        </span>
                        <svg
                          className={`w-4 h-4 text-gray-400 ${isRTL ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Link>
                    </div>
                    
                    {/* Logout Button */}
                    <div className="p-2 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors text-sm font-medium"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        {t('common.signOut')}
                      </button>
                    </div>
                  </div>
                )}
                </div>
              ) : null
            ) : (
              <Link
                to={ROUTES.LOGIN}
                className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors font-medium"
                aria-label={translations.loginSignup}
              >
                {translations.loginSignup}
              </Link>
            )}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <svg className="w-6 h-6 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </button>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle language"
            >
              <svg className="w-6 h-6 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

